import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetOrderQuery, useUploadOrderReceiptMutation } from "../store/api";
import styled from "styled-components";

const Container = styled.div`
  background: var(--bg-primary);
  min-height: 100vh;
  padding: 1.5rem;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu",
    "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s;

  &:hover {
    color: #b2b2b2;
    background: #f0f9f4;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;
  max-width: 1400px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  background: var(--bg-card);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
`;

const RightPanel = styled.div`
  background: var(--bg-card);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
`;

const OrderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const OrderTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
`;

const OrderId = styled.span`
  background: var(--bg-secondary);
  color: #475569;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.875rem;
`;

const StatusSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const StatusItem = styled.div`
  text-align: center;
`;

const StatusLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const StatusValue = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const StatusBadge = styled.span<{ status: string }>`
  background: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case "completed":
        return "#dcfce7";
      case "pending":
        return "#fef3c7";
      case "processing":
        return "#dbeafe";
      case "failed":
      case "cancelled":
        return "#fee2e2";
      default:
        return "#f1f5f9";
    }
  }};
  color: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case "completed":
        return "#166534";
      case "pending":
        return "#92400e";
      case "processing":
        return "#1e40af";
      case "failed":
      case "cancelled":
        return "#991b1b";
      default:
        return "#475569";
    }
  }};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
`;

const AmountSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--bg-primary);
  border-radius: 12px;
`;

const AmountItem = styled.div`
  text-align: center;
`;

const AmountLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const AmountValue = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) =>
    props.positive ? "#B2B2B2" : props.negative ? "#ef4444" : "#1e293b"};
`;

const DetailsSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
`;

const DetailValue = styled.span<{ mono?: boolean }>`
  color: var(--text-primary);
  font-weight: 500;
  font-family: ${(props) =>
    props.mono ? "'JetBrains Mono', monospace" : "inherit"};
  word-break: break-all;
  overflow-wrap: break-word;
`;

const RequisitesSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--bg-primary);
  border-radius: 12px;
`;

const RequisiteItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--bg-card);
  border-radius: 8px;
  border: 1px solid var(--border-color);
`;

const BankIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #b2b2b2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
  font-weight: 600;
`;

const RequisiteDetails = styled.div`
  flex: 1;
`;

const RequisiteNumber = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  font-family: "JetBrains Mono", monospace;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const RequisitePhone = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.125rem;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: var(--status-error-bg);
  color: var(--status-error-text);
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
`;

// Стили для истории изменений
const HistorySection = styled.div`
  margin-top: 2rem;
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const HistoryRow = styled.tr`
  border-bottom: 1px solid var(--border-color);
`;

const HistoryCell = styled.td`
  padding: 1rem 0.5rem;
  font-size: 0.875rem;
  vertical-align: top;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
`;

const HistoryOperation = styled.div`
  color: #3b82f6;
  margin-bottom: 0.25rem;
  font-weight: 500;
`;

const HistoryUser = styled.div`
  color: var(--text-secondary);
  font-size: 0.75rem;
`;

const HistoryTime = styled.div`
  color: var(--text-secondary);
  text-align: center;
`;

const HistoryAmount = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-weight: 500;
  color: ${(props) =>
    props.positive ? "#B2B2B2" : props.negative ? "#ef4444" : "#1e293b"};
`;

const HistoryBalance = styled.div`
  text-align: right;
  font-family: "JetBrains Mono", monospace;
  color: var(--text-primary);
`;

// Стили для прикрепленных чеков
const AttachmentTabs = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 1rem;
`;

const AttachmentTab = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  color: ${(props) => (props.active ? "#B2B2B2" : "#64748b")};
  padding: 1rem 2rem;
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.active ? "#B2B2B2" : "transparent")};
  font-size: 1rem;
  font-weight: 500;

  &:hover {
    color: #b2b2b2;
  }
`;

const AttachmentContent = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const AttachmentIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const UploadButton = styled.button`
  background: #b2b2b2;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  margin-top: 1rem;

  &:hover {
    background: #cecece;
  }
`;

const ReceiptContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  max-height: 400px;
  overflow-y: auto;
`;

const ReceiptsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 300px;
`;

const ReceiptItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--border-divider);
  border-radius: 0.5rem;
  background: var(--bg-card);
  width: 100%;
`;

const ReceiptImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 0.375rem;
  object-fit: contain;
`;

const ReceiptInfo = styled.div`
  text-align: center;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const DownloadButton = styled.button`
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-input);
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
`;

function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeAttachmentTab, setActiveAttachmentTab] = React.useState<
    "buyer" | "trader"
  >("buyer");
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string>("");

  const [uploadOrderReceipt] = useUploadOrderReceiptMutation();

  const {
    data: order,
    error,
    isLoading,
  } = useGetOrderQuery(id!, {
    skip: !id,
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    try {
      return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(dateString));
    } catch {
      return dateString;
    }
  };

  const getStatusText = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "Ожидает";
      case "processing":
        return "В обработке";
      case "completed":
        return "Завершен";
      case "cancelled":
        return "Отменен";
      case "failed":
        return "Неудача";
      case "expired":
        return "Истек";
      default:
        return status;
    }
  };

  const formatAmount = (amount: number, currency: string = "RUB") => {
    return (
      new Intl.NumberFormat("ru-RU", {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount) + ` ${currency}`
    );
  };

  const getReceiptsFromOrder = (order: any, type: "buyer" | "trader") => {
    try {
      const metadata = order?.metadata ? JSON.parse(order.metadata) : {};
      const receipts = metadata?.receipts?.[type];

      // Если это массив, возвращаем как есть
      if (Array.isArray(receipts)) {
        return receipts;
      }

      // Если это один объект, возвращаем как массив
      if (receipts && typeof receipts === "object") {
        return [receipts];
      }

      return [];
    } catch {
      return [];
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    setIsUploading(true);
    setUploadError("");

    try {
      await uploadOrderReceipt({
        orderId: id,
        file,
        type: "buyer", // Мерчант может загружать только чеки покупателя
      }).unwrap();

      // Сбрасываем значение input для возможности повторной загрузки
      event.target.value = "";
      setUploadError("");
    } catch (error: any) {
      setUploadError(error?.data?.message || "Ошибка при загрузке чека");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadFile = async (receipt: any, orderId: string) => {
    try {
      const baseUrl =
        process.env.REACT_APP_API_URL || "http://localhost:3001/api";
      const token = localStorage.getItem("token");

      // Попробуем использовать новый безопасный эндпоинт
      const downloadUrl = `${baseUrl}/orders/${orderId}/receipt/${activeAttachmentTab}/download/${receipt.filename}`;

      console.log("Attempting to download:", downloadUrl);
      console.log("Receipt:", receipt);

      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Download response status:", response.status);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = receipt.originalName || receipt.filename;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      }

      // Если эндпоинт не работает, используем прямую ссылку как fallback
      console.warn("Безопасный эндпоинт недоступен, используем прямую ссылку");
      const directLink = document.createElement("a");
      directLink.href = receipt.path;
      directLink.download = receipt.originalName || receipt.filename;
      directLink.target = "_blank";
      document.body.appendChild(directLink);
      directLink.click();
      document.body.removeChild(directLink);
    } catch (error) {
      console.error("Ошибка при скачивании файла:", error);
      setUploadError("Ошибка при скачивании файла");
    }
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingSpinner>Загрузка данных ордера...</LoadingSpinner>
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container>
        <Header>
          <BackButton onClick={() => navigate(-1)}>← Назад</BackButton>
        </Header>
        <ErrorMessage>
          Ошибка при загрузке данных ордера. Попробуйте еще раз.
        </ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)}>← Назад</BackButton>
      </Header>

      <MainContent>
        <LeftPanel>
          <OrderHeader>
            <OrderTitle>Ордер</OrderTitle>
            <OrderId>#{order.invId}</OrderId>
          </OrderHeader>

          <StatusSection>
            <StatusItem>
              <StatusLabel>Статус</StatusLabel>
              <StatusValue>
                <StatusBadge status={order.status}>
                  {getStatusText(order.status)}
                </StatusBadge>
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel>Тип операции</StatusLabel>
              <StatusValue style={{ color: "#B2B2B2" }}>
                {order.type === "DEPOSIT" ? "Пополнение" : "Вывод"}
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel>Дата создания</StatusLabel>
              <StatusValue>{formatDate(order.createdAt)}</StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel>Последнее обновление</StatusLabel>
              <StatusValue>{formatDate(order.updatedAt)}</StatusValue>
            </StatusItem>
          </StatusSection>

          <AmountSection>
            <AmountItem>
              <AmountLabel>Сумма ордера</AmountLabel>
              <AmountValue>
                {formatAmount(order.amount, order.currency)}
              </AmountValue>
            </AmountItem>
            {order.factAmount && (
              <AmountItem>
                <AmountLabel>Фактическая сумма</AmountLabel>
                <AmountValue positive>
                  {formatAmount(order.factAmount, order.currency)}
                </AmountValue>
              </AmountItem>
            )}
            {order.usdtAmount && (
              <AmountItem>
                <AmountLabel>Сумма в USDT</AmountLabel>
                <AmountValue>
                  {formatAmount(order.usdtAmount, "USDT")}
                </AmountValue>
              </AmountItem>
            )}
            {order.exchangeRate && (
              <AmountItem>
                <AmountLabel>Курс обмена</AmountLabel>
                <AmountValue>{order.exchangeRate.toFixed(4)}</AmountValue>
              </AmountItem>
            )}
          </AmountSection>

          <DetailsSection>
            <SectionTitle>Детали ордера</SectionTitle>
            <DetailsGrid>
              <DetailItem>
                <DetailLabel>ID ордера</DetailLabel>
                <DetailValue mono={true}>{order.id}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Внешний ID</DetailLabel>
                <DetailValue mono={true}>{order.invId}</DetailValue>
              </DetailItem>
              {order.requisite?.tag && (
                <DetailItem>
                  <DetailLabel>Название реквизита</DetailLabel>
                  <DetailValue>{order.requisite.tag}</DetailValue>
                </DetailItem>
              )}
              {order.bank && (
                <DetailItem>
                  <DetailLabel>Банк</DetailLabel>
                  <DetailValue>{order.bank}</DetailValue>
                </DetailItem>
              )}
              {order.cardNumber && (
                <DetailItem>
                  <DetailLabel>Номер карты</DetailLabel>
                  <DetailValue mono={true}>{order.cardNumber}</DetailValue>
                </DetailItem>
              )}
              {order.phoneNumber && (
                <DetailItem>
                  <DetailLabel>Номер телефона</DetailLabel>
                  <DetailValue>{order.phoneNumber}</DetailValue>
                </DetailItem>
              )}
              {order.accountNumber && (
                <DetailItem>
                  <DetailLabel>Номер счета</DetailLabel>
                  <DetailValue mono={true}>{order.accountNumber}</DetailValue>
                </DetailItem>
              )}
              {order.bankIdCode && (
                <DetailItem>
                  <DetailLabel>БИК банка</DetailLabel>
                  <DetailValue mono={true}>{order.bankIdCode}</DetailValue>
                </DetailItem>
              )}
              {order.recipientFio && (
                <DetailItem>
                  <DetailLabel>ФИО получателя</DetailLabel>
                  <DetailValue>{order.recipientFio}</DetailValue>
                </DetailItem>
              )}
              {order.callbackUrl && (
                <DetailItem>
                  <DetailLabel>Callback URL</DetailLabel>
                  <DetailValue mono={true}>{order.callbackUrl}</DetailValue>
                </DetailItem>
              )}
              {order.expiresAt && (
                <DetailItem>
                  <DetailLabel>Срок действия</DetailLabel>
                  <DetailValue>{formatDate(order.expiresAt)}</DetailValue>
                </DetailItem>
              )}
              {order.lastCallbackAt && (
                <DetailItem>
                  <DetailLabel>Последний callback</DetailLabel>
                  <DetailValue>{formatDate(order.lastCallbackAt)}</DetailValue>
                </DetailItem>
              )}
            </DetailsGrid>
          </DetailsSection>

          {(order.cardNumber || order.phoneNumber) && (
            <RequisitesSection>
              <SectionTitle>Реквизиты для оплаты</SectionTitle>
              <RequisiteItem>
                <BankIcon>
                  {order.bank ? order.bank.charAt(0).toUpperCase() : "Б"}
                </BankIcon>
                <RequisiteDetails>
                  {order.requisite?.tag && (
                    <RequisitePhone style={{ fontWeight: 700, color: '#0f172a' }}>{order.requisite.tag}</RequisitePhone>
                  )}
                  {order.cardNumber && (
                    <RequisiteNumber>{order.cardNumber}</RequisiteNumber>
                  )}
                  {order.phoneNumber && (
                    <RequisitePhone>{order.phoneNumber}</RequisitePhone>
                  )}
                  {order.bank && <RequisitePhone>{order.bank}</RequisitePhone>}
                </RequisiteDetails>
              </RequisiteItem>
            </RequisitesSection>
          )}

          {/* История изменений сделки */}
          <HistorySection>
            <SectionTitle>История изменений сделки</SectionTitle>
            <HistoryTable>
              <thead>
                <HistoryRow>
                  <HistoryCell>
                    <strong>Операция</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Пользователь</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Время</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Сумма</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Баланс до → после</strong>
                  </HistoryCell>
                </HistoryRow>
              </thead>
              <tbody>
                {/* Начисление за успешный ордер */}
                {order.status === "COMPLETED" && (
                  <HistoryRow>
                    <HistoryCell>
                      <HistoryOperation>
                        Пополнение - Начисление за успешный ордер
                      </HistoryOperation>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryUser>Система</HistoryUser>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryTime>
                        {formatDate(order.updatedAt || order.createdAt)}
                      </HistoryTime>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryAmount positive>
                        +
                        {(
                          (order.usdtAmount ||
                            order.amount / (order.exchangeRate || 80.36)) -
                          (order.merchantFee || 0)
                        ).toFixed(2)}{" "}
                        USDT
                      </HistoryAmount>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryBalance>
                        Получено{" "}
                        {(
                          (order.usdtAmount ||
                            order.amount / (order.exchangeRate || 80.36)) -
                          (order.merchantFee || 0)
                        ).toFixed(2)}{" "}
                        USDT
                      </HistoryBalance>
                    </HistoryCell>
                  </HistoryRow>
                )}
              </tbody>
            </HistoryTable>
          </HistorySection>
        </LeftPanel>

        <RightPanel>
          <SectionTitle>Прикрепленные чеки</SectionTitle>
          <AttachmentTabs>
            <AttachmentTab
              active={activeAttachmentTab === "buyer"}
              onClick={() => setActiveAttachmentTab("buyer")}
            >
              Покупатель
            </AttachmentTab>
            <AttachmentTab
              active={activeAttachmentTab === "trader"}
              onClick={() => setActiveAttachmentTab("trader")}
            >
              Трейдер
            </AttachmentTab>
          </AttachmentTabs>
          <AttachmentContent>
            {(() => {
              const receipts = getReceiptsFromOrder(order, activeAttachmentTab);

              if (receipts.length > 0) {
                return (
                  <ReceiptContainer>
                    <ReceiptsList>
                      {receipts.map((receipt: any, index: number) => {
                        const isImage = receipt.mimetype?.startsWith("image/");
                        return (
                          <ReceiptItem key={index}>
                            {isImage ? (
                              <ReceiptImage src={receipt.path} alt="Чек" />
                            ) : (
                              <AttachmentIcon>PDF</AttachmentIcon>
                            )}
                            <div
                              style={{
                                textAlign: "center",
                                marginBottom: "0.5rem",
                              }}
                            >
                              <strong>
                                {receipt.originalName || `Чек ${index + 1}`}
                              </strong>
                            </div>
                            <DownloadButton
                              onClick={() => handleDownloadFile(receipt, id!)}
                            >
                              Скачать файл
                            </DownloadButton>
                            <ReceiptInfo>
                              Загружен:{" "}
                              {new Date(receipt.uploadedAt).toLocaleDateString(
                                "ru-RU"
                              )}
                            </ReceiptInfo>
                          </ReceiptItem>
                        );
                      })}
                    </ReceiptsList>
                    {activeAttachmentTab === "buyer" && (
                      <>
                        <input
                          type="file"
                          id="receipt-upload"
                          style={{ display: "none" }}
                          accept="image/*,.pdf"
                          onChange={handleFileUpload}
                        />
                        <UploadButton
                          as="label"
                          htmlFor="receipt-upload"
                          style={{
                            cursor: isUploading ? "not-allowed" : "pointer",
                          }}
                        >
                          {isUploading ? "Загрузка..." : "Добавить еще чек"}
                        </UploadButton>
                      </>
                    )}
                  </ReceiptContainer>
                );
              }

              return (
                <>
                  <AttachmentIcon>DOC</AttachmentIcon>
                  <div>
                    {activeAttachmentTab === "buyer"
                      ? "Чеки от покупателя еще не прикреплены"
                      : "Чеки от трейдера еще не прикреплены"}
                  </div>
                  {uploadError && (
                    <div
                      style={{
                        color: "#ef4444",
                        fontSize: "0.875rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      {uploadError}
                    </div>
                  )}
                  {activeAttachmentTab === "buyer" && (
                    <>
                      <input
                        type="file"
                        id="receipt-upload"
                        style={{ display: "none" }}
                        accept="image/*,.pdf"
                        onChange={handleFileUpload}
                      />
                      <UploadButton
                        as="label"
                        htmlFor="receipt-upload"
                        style={{
                          cursor: isUploading ? "not-allowed" : "pointer",
                        }}
                      >
                        {isUploading
                          ? "Загрузка..."
                          : "Прикрепить чек покупателя"}
                      </UploadButton>
                    </>
                  )}
                </>
              );
            })()}
          </AttachmentContent>
        </RightPanel>
      </MainContent>
    </Container>
  );
}

export default OrderDetail;
